import React from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextCharLimit,
  Typography,
} from 'views/components';
import {Add as AddIcon, Delete as DeleteIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

// Loom's limits for family questions (CQ-752).
const MAX_QUESTIONS = 5;
const MAX_LENGTH = 200;

const InterviewQuestionsDialog = (props) => {
  const {questions, onClose, onSave} = props;

  const [texts, setTexts] = React.useState(questions.map((question) => question.text));
  const [saving, setSaving] = React.useState(false);

  const canSave = texts.every((text) => text.trim()) && !saving;

  const submit = async () => {
    setSaving(true);
    await onSave(texts.map((text) => text.trim()));
    setSaving(false);
  };

  return (
    <Dialog onClose={onClose}>
      <DialogTitle onClose={onClose}>
        <Translate expression="career.jobDetail.InterviewQuestions.Dialog.Title" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{pt: 1}}>
          <Typography variant="body2" color="text.secondary">
            <Translate expression="career.jobDetail.InterviewQuestions.Dialog.Note" substitute={{max: MAX_QUESTIONS}} />
          </Typography>
          {
            texts.map((text, index) => (
              <Stack key={index} direction="row" spacing={1} sx={{alignItems: 'flex-start'}}>
                <TextCharLimit
                  multiline
                  autoFocus={index === texts.length - 1 && !text}
                  charLimit={MAX_LENGTH}
                  label={translate('career.jobDetail.InterviewQuestions.Dialog.Question', {number: index + 1})}
                  value={text}
                  onChange={(value) => setTexts((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)))}
                />
                <IconButton
                  aria-label={translate('career.jobDetail.InterviewQuestions.Dialog.Delete', {number: index + 1})}
                  onClick={() => setTexts((current) => current.filter((item, itemIndex) => itemIndex !== index))}
                  sx={{mt: 1}}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))
          }
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={texts.length >= MAX_QUESTIONS}
            sx={{alignSelf: 'flex-start'}}
            onClick={() => setTexts((current) => [...current, ''])}
          >
            <Translate expression="career.jobDetail.InterviewQuestions.Dialog.Add" />
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          <Translate expression="generic.Cancel" />
        </Button>
        <Button disabled={!canSave} onClick={submit}>
          <Translate expression="generic.Save" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InterviewQuestionsDialog.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default React.memo(InterviewQuestionsDialog);
